import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowDown, ChevronRight } from "lucide-react";
import { STATS, MEDIA, COUNTRIES } from "../data/site";
import { CATEGORIES } from "../data/products";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { usePageTitle } from "../lib/usePageTitle";
import { useLocalePath } from "../lib/useLocalePath";
import { useCountryName } from "../lib/useCountryName";

const STEP_KEYS = ["s1", "s2", "s3", "s4"];

export default function Home() {
    usePageTitle("meta.home");
    const { t } = useTranslation();
    const lp = useLocalePath();
    const countryName = useCountryName();

    return (
        <main data-testid="page-home">
            {/* HERO */}
            <section
                data-testid="home-hero"
                className="relative min-h-screen w-full overflow-hidden"
            >
                <img
                    src={MEDIA.heroPrimary}
                    alt={t("gallery.captions.quarry_sunrise")}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D]/85 via-[#0D0D0D]/55 to-[#0D0D0D]" />
                <div className="absolute inset-0 grain-overlay" />

                <div className="relative h-full min-h-screen px-6 md:px-12 lg:px-20 pt-40 pb-24 flex flex-col justify-between">
                    <div className="max-w-5xl">
                        <p className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#C5A059] soft-in">
                            {t("home.hero.overline")}
                        </p>
                        <h1 className="mt-8 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#E8E3D9] leading-[1.05] tracking-tight soft-in">
                            <span className="block">{t("home.hero.line1a")}</span>
                            <span className="block italic text-[#C5A059]">
                                {t("home.hero.line1b")}
                            </span>
                            <span className="block mt-6 md:mt-8">
                                {t("home.hero.line2a")}
                            </span>
                            <span className="block italic text-[#C5A059]">
                                {t("home.hero.line2b")}
                            </span>
                            <span className="block mt-6 md:mt-8">
                                {t("home.hero.line3a")}
                            </span>
                            <span className="block italic text-[#C5A059]">
                                {t("home.hero.line3b")}
                            </span>
                        </h1>
                        <p className="mt-10 max-w-2xl text-[#A39E93] text-base md:text-lg leading-relaxed font-light soft-in">
                            {t("home.hero.body")}
                        </p>
                        <div className="mt-12 flex flex-wrap items-center gap-4 soft-in">
                            <Link
                                to={lp("/contact")}
                                data-testid="hero-cta-quote"
                                className="inline-flex items-center gap-3 bg-[#C5A059] text-[#0D0D0D] px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-medium hover:bg-[#E8E3D9] transition-colors"
                            >
                                {t("home.hero.cta_quote")}
                                <ArrowRight size={14} />
                            </Link>
                            <Link
                                to={lp("/products")}
                                data-testid="hero-cta-products"
                                className="inline-flex items-center gap-3 border border-[#C5A059] text-[#C5A059] px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-medium hover:bg-[#C5A059] hover:text-[#0D0D0D] transition-colors"
                            >
                                {t("home.hero.cta_products")}
                            </Link>
                        </div>
                    </div>

                    <div className="hidden md:flex items-center gap-3 text-[#A39E93]">
                        <ArrowDown size={14} />
                        <span className="text-[10px] uppercase tracking-[0.35em]">
                            {t("home.hero.scroll")}
                        </span>
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section
                data-testid="home-stats"
                className="bg-[#0D0D0D] border-y border-[#2C2C2C]"
            >
                <div className="px-6 md:px-12 lg:px-20 py-20 md:py-28 grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-6">
                    {STATS.map((s, i) => (
                        <Reveal key={s.key} delay={i * 90}>
                            <div data-testid={s.testid}>
                                <p className="font-heading text-5xl md:text-6xl lg:text-7xl text-[#E8E3D9] leading-none tracking-tighter">
                                    {s.value}
                                </p>
                                <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
                                    {t(`home.stats.${s.key}`)}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* INTRO + IMAGE OVERLAP */}
            <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    <div className="lg:col-span-5 lg:sticky lg:top-32">
                        <Reveal>
                            <SectionHeading
                                overline={t("home.intro.overline")}
                                title={t("home.intro.title")}
                                kicker={t("home.intro.kicker")}
                            />
                            <Link
                                to={lp("/about")}
                                data-testid="home-about-link"
                                className="mt-10 inline-flex items-center gap-3 text-[#C5A059] text-[11px] uppercase tracking-[0.3em] hover:gap-5 transition-all"
                            >
                                {t("home.intro.cta")} <ChevronRight size={14} />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-2 gap-4 md:gap-6">
                        <Reveal className="col-span-2">
                            <img
                                src={MEDIA.slab1}
                                alt={t("gallery.captions.pbg_slab")}
                                className="w-full h-72 md:h-96 object-cover"
                            />
                        </Reveal>
                        <Reveal delay={120}>
                            <img
                                src={MEDIA.mfg1}
                                alt={t("gallery.captions.cutting_line")}
                                className="w-full h-56 md:h-72 object-cover"
                            />
                        </Reveal>
                        <Reveal delay={200} className="mt-12">
                            <img
                                src={MEDIA.mfg2}
                                alt={t("gallery.captions.polishing_line")}
                                className="w-full h-56 md:h-72 object-cover"
                            />
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* FEATURED CATEGORIES */}
            <section
                data-testid="home-categories"
                className="bg-[#0D0D0D] border-t border-[#2C2C2C]"
            >
                <div className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
                    <Reveal>
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
                            <SectionHeading
                                overline={t("home.categories.overline")}
                                title={t("home.categories.title")}
                                kicker={t("home.categories.kicker")}
                            />
                            <Link
                                to={lp("/products")}
                                data-testid="home-view-all-products"
                                className="inline-flex items-center gap-3 text-[#C5A059] text-[11px] uppercase tracking-[0.3em]"
                            >
                                {t("home.categories.view_all")}{" "}
                                <ArrowRight size={14} />
                            </Link>
                        </div>
                    </Reveal>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {CATEGORIES.map((c, i) => (
                            <Reveal key={c.key} delay={i * 90} className="h-full">
                                <Link
                                    to={lp(`/products?category=${c.key}`)}
                                    data-testid={`category-card-${c.key}`}
                                    className="group flex flex-col h-full overflow-hidden bg-[#1A1A1A] border border-[#2C2C2C] hover:border-[#C5A059] transition-colors"
                                >
                                    <div className="aspect-[16/10] overflow-hidden shrink-0">
                                        <img
                                            src={c.image}
                                            alt={t(`categories.${c.i18nKey}.name`)}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                                        />
                                    </div>
                                    <div className="p-6 md:p-8 flex items-end justify-between gap-6 flex-1">
                                        <div>
                                            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">
                                                {t(`categories.${c.i18nKey}.tagline`)}
                                            </p>
                                            <h3 className="mt-3 font-heading text-3xl text-[#E8E3D9] tracking-tight">
                                                {t(`categories.${c.i18nKey}.name`)}
                                            </h3>
                                            <p className="mt-3 text-sm text-[#A39E93] font-light leading-relaxed max-w-md">
                                                {t(`categories.${c.i18nKey}.description`)}
                                            </p>
                                        </div>
                                        <ChevronRight
                                            className="text-[#C5A059] shrink-0 group-hover:translate-x-1 transition-transform"
                                            size={20}
                                        />
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCESS TEASER */}
            <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <Reveal className="lg:col-span-6 relative">
                        <img
                            src={MEDIA.heroSecondary}
                            alt={t("gallery.captions.quarry_wall")}
                            className="w-full h-[60vh] object-cover"
                        />
                        <div className="absolute -bottom-8 -right-4 lg:-right-12 w-2/3 hidden md:block">
                            <img
                                src={MEDIA.arch1}
                                alt={t("gallery.captions.arch_luxury")}
                                className="w-full h-72 object-cover border-8 border-[#0D0D0D]"
                            />
                        </div>
                    </Reveal>
                    <Reveal className="lg:col-span-6 lg:pl-8" delay={150}>
                        <SectionHeading
                            overline={t("home.process_teaser.overline")}
                            title={t("home.process_teaser.title")}
                            kicker={t("home.process_teaser.kicker")}
                        />
                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
                            {STEP_KEYS.map((key, idx) => {
                                const n = String(idx + 1).padStart(2, "0");
                                return (
                                    <div
                                        key={key}
                                        data-testid={`process-step-${n}`}
                                        className="border-l border-[#C5A059] pl-5"
                                    >
                                        <p className="font-heading text-2xl text-[#C5A059]">
                                            {n}
                                        </p>
                                        <p className="mt-2 text-[#E8E3D9] uppercase tracking-[0.25em] text-xs">
                                            {t(`home.process_teaser.steps.${key}`)}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                        <Link
                            to={lp("/process")}
                            data-testid="home-process-link"
                            className="mt-12 inline-flex items-center gap-3 border border-[#C5A059] text-[#C5A059] px-7 py-3.5 text-[11px] uppercase tracking-[0.25em] hover:bg-[#C5A059] hover:text-[#0D0D0D] transition-colors"
                        >
                            {t("home.process_teaser.cta")}
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* MARQUEE OF COUNTRIES */}
            <section
                data-testid="home-countries-marquee"
                className="bg-[#1A1A1A] border-y border-[#2C2C2C] overflow-hidden"
            >
                <div className="px-6 md:px-12 lg:px-20 py-10 flex flex-col gap-6">
                    <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
                        {t("home.marquee.shipped_to")}
                    </p>
                    <div className="overflow-hidden">
                        <div className="marquee-track">
                            {[...COUNTRIES, ...COUNTRIES].map((c, i) => (
                                <span
                                    key={`${c.code}-${i}`}
                                    className="font-heading italic text-2xl md:text-4xl text-[#E8E3D9] px-8 inline-flex items-center"
                                >
                                    <img
                                        src={`https://flagcdn.com/h40/${c.code}.png`}
                                        alt=""
                                        className="h-6 md:h-8 w-auto mr-4 rounded-sm"
                                        loading="lazy"
                                    />
                                    {countryName(c.code, c.name)}{" "}
                                    <span className="text-[#C5A059] mx-3">·</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
                <Reveal>
                    <div className="bg-[#1A1A1A] border-l-2 border-[#C5A059] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                        <div className="md:col-span-8">
                            <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
                                {t("home.cta_final.overline")}
                            </p>
                            <h3 className="mt-4 font-heading text-3xl md:text-5xl text-[#E8E3D9] leading-tight tracking-tight">
                                {t("home.cta_final.title")}
                            </h3>
                        </div>
                        <div className="md:col-span-4 flex flex-col gap-4">
                            <Link
                                to={lp("/contact")}
                                data-testid="home-cta-final-quote"
                                className="inline-flex items-center justify-center gap-3 bg-[#C5A059] text-[#0D0D0D] px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-medium hover:bg-[#E8E3D9] transition-colors"
                            >
                                {t("home.cta_final.cta_quote")}{" "}
                                <ArrowRight size={14} />
                            </Link>
                            <Link
                                to={lp("/contact")}
                                data-testid="home-cta-catalog"
                                className="inline-flex items-center justify-center gap-3 border border-[#C5A059] text-[#C5A059] px-8 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#C5A059] hover:text-[#0D0D0D] transition-colors"
                            >
                                {t("home.cta_final.cta_catalog")}
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </main>
    );
}
